// Feste Wertelisten. Die Werte stehen genauso als CHECK-Constraints in
// server/migrationen/0001_grundschema.sql – wer hier etwas ergänzt, braucht
// eine neue Migration.
//
// Bewusst `as const`-Arrays statt TypeScript-enums: Node führt die .ts-Dateien
// per Type-Stripping direkt aus, und enums sind dort nicht erlaubt.

export const SURVEY_STATUS = [
  "entwurf",
  "eingereicht",
  "in_auswertung",
  "fertig",
  "geprueft",
  "fehler",
] as const;
export type SurveyStatus = (typeof SURVEY_STATUS)[number];

export const SURVEY_STATUS_LABEL: Record<SurveyStatus, string> = {
  entwurf: "In Erfassung",
  eingereicht: "Eingereicht",
  in_auswertung: "Wird ausgewertet",
  fertig: "Bericht fertig",
  geprueft: "Geprüft",
  fehler: "Fehler bei der Auswertung",
};

export const ROOM_TYPES = [
  "flur",
  "wohnen",
  "schlafen",
  "kueche",
  "bad",
  "kind",
  "arbeit",
  "esszimmer",
  "abstellraum",
  "keller",
  "dachboden",
  "garage",
  "balkon",
  "sonstiges",
] as const;
export type RoomType = (typeof ROOM_TYPES)[number];

export const ROOM_TYPE_LABEL: Record<RoomType, string> = {
  flur: "Flur / Diele",
  wohnen: "Wohnzimmer",
  schlafen: "Schlafzimmer",
  kueche: "Küche",
  bad: "Bad / WC",
  kind: "Kinderzimmer",
  arbeit: "Arbeitszimmer",
  esszimmer: "Esszimmer",
  abstellraum: "Abstellraum",
  keller: "Keller",
  dachboden: "Dachboden",
  garage: "Garage",
  balkon: "Balkon / Terrasse",
  sonstiges: "Sonstiger Raum",
};

/** Nebenräume werden in der Erfassung getrennt abgefragt. */
export const SIDE_ROOM_TYPES: readonly RoomType[] = ["keller", "dachboden", "garage", "balkon"];

/**
 * Abschnitte der Fotoaufnahme in fester Reihenfolge. Flur seit 14.10.2026 nach Wänden (Betreiber: „alle 2–3 Meter
 * in Gehrichtung“ war nicht zuverlässig): flur_links/flur_rechts = Wand quer und abschnittsweise fotografiert,
 * flur_zurueck = vom Flurende zurück zum Eingang. "flur" (in Gehrichtung) bleibt für ältere Aufnahmen gültig.
 */
export const PHOTO_STEPS = [
  "tuer",
  "flur",
  "flur_knick",
  "flur_links",
  "flur_rechts",
  "flur_zurueck",
  "rundum",
  "schrank_offen",
  // Räume nach Wänden (15.10.2026): feste Reihenfolge ab der Zimmertür, siehe ROOM_WALL_STEPS.
  "wand_gegenueber",
  "wand_rechts",
  "wand_links",
  "wand_tuer",
  "wand_nische",
  // Kleine Räume (Bad, Abstellraum, Balkon): aus der Tür hinein, aus der hintersten Ecke zurück.
  "klein_rein",
  "klein_zurueck",
  // Lampen ab- und anbauen (15.10.2026): ein Foto je Lampe, Handy nach oben.
  "lampe",
] as const;
export type PhotoStep = (typeof PHOTO_STEPS)[number];
/** Fotoarten des Flurs (Eingang und Wände), in der Reihenfolge der Aufnahme. */
// flur_knick (14.10.2026): an einer Ecke bzw. am Abzweig in den neuen Abschnitt hinein (wirkt wie „tuer“ für Abschnitt 1).
export const HALL_PHOTO_STEPS = ["tuer", "flur", "flur_knick", "flur_links", "flur_rechts", "flur_zurueck"] as const satisfies readonly PhotoStep[];

export const MARK_ACTIONS = ["bleibt", "entsorgen", "abbau_aufbau"] as const;
export type MarkAction = (typeof MARK_ACTIONS)[number];

/**
 * Was der Kunde beim Markieren wählen kann. 08.10.2026 nur "bleibt" und "entsorgen"; seit 16.10.2026 (Betreiber: die
 * Firma muss wissen, ob Ab- und Aufbau gewünscht ist, bepreisen kann sie ihn wie sie will) wieder auch
 * "abbau_aufbau" – im Bericht als Kundenwunsch ausgewiesen, die Firma kann jede Position ändern.
 */
export const CUSTOMER_MARK_ACTIONS = ["bleibt", "entsorgen", "abbau_aufbau"] as const satisfies readonly MarkAction[];

export const MARK_ACTION_LABEL: Record<MarkAction, string> = {
  bleibt: "Kommt nicht mit",
  entsorgen: "Entsorgen",
  abbau_aufbau: "Ab- und aufbauen",
};

/** Was mit einer Inventarposition passiert. "mit" = wird transportiert. */
export const ITEM_ACTIONS = ["mit", "bleibt", "entsorgen", "abbau_aufbau"] as const;
export type ItemAction = (typeof ITEM_ACTIONS)[number];

export const ITEM_FLAGS = ["einbau", "klavier", "tresor", "schwer", "zerbrechlich", "sperrig"] as const;
export type ItemFlag = (typeof ITEM_FLAGS)[number];
export const ITEM_ACTION_LABEL: Record<ItemAction, string> = {
  mit: "Kommt mit",
  bleibt: "Bleibt",
  entsorgen: "Entsorgen",
  abbau_aufbau: "Ab- und Aufbau",
};
export const ITEM_FLAG_LABEL: Record<ItemFlag, string> = {
  einbau: "Einbau",
  klavier: "Klavier",
  tresor: "Tresor",
  schwer: "Schwer",
  zerbrechlich: "Zerbrechlich",
  sperrig: "Sperrig",
};

export const CONFIDENCE = ["hoch", "mittel", "niedrig"] as const;
export type Confidence = (typeof CONFIDENCE)[number];

export const ITEM_SOURCES = ["ki", "manuell"] as const;
export type ItemSource = (typeof ITEM_SOURCES)[number];

export const INVITE_CHANNELS = ["whatsapp", "sms", "email", "link"] as const;
export type InviteChannel = (typeof INVITE_CHANNELS)[number];

export const SERVICES = ["kueche", "lampen", "packservice", "kartonanlieferung"] as const;
export type Service = (typeof SERVICES)[number];

export const SERVICE_LABEL: Record<Service, string> = {
  kueche: "Küche ab- und aufbauen",
  lampen: "Lampen ab- und anbringen",
  packservice: "Packservice (wir packen ein)",
  kartonanlieferung: "Kartons vorab anliefern",
};

/** Wie ein Bericht freigeschaltet wurde (siehe server/src/kontingent.ts). */
export const UNLOCK_TYPES = ["kontingent", "probe", "upgrade", "privat", "zusatz", "frei", "demo"] as const;
/** Freier Zugang (Prüfkonto): höchstens so viele ausgewertete Aufnahmen je Tag (deutsche Zeit). */
export const FREE_ACCESS_DAILY_LIMIT = 5;
export type UnlockType = (typeof UNLOCK_TYPES)[number];

/** Antwort auf "Gibt es hier große Schränke?" (rooms.cabinets, Migration 0010). */
export const CABINET_ANSWERS = ["ja", "nein"] as const;
export type CabinetAnswer = (typeof CABINET_ANSWERS)[number];

/**
 * Räume nach Wänden (15.10.2026, Betreiber): Der Kunde steht in der Zimmertür und fotografiert immer erst die
 * gegenüberliegende Wand, dann die rechte, dann die linke; danach von der anderen Seite die Wand mit der Tür. Lange Wände
 * in überlappenden Teilen. So weiß jedes Foto, welche Wand es zeigt (Türen je Wand, Skizze, Möbelplan). Ein Raum mit
 * Ecke oder Nische bekommt Zusatzfotos (wand_nische). Kleine Räume: zwei Fotos (klein_rein, klein_zurueck).
 * Ältere Aufnahmen mit freien Rundum-Fotos (rundum) bleiben gültig.
 */
export const ROOM_WALL_STEPS = ["wand_gegenueber", "wand_rechts", "wand_links", "wand_tuer"] as const satisfies readonly PhotoStep[];
export const ROOM_SMALL_STEPS = ["klein_rein", "klein_zurueck"] as const satisfies readonly PhotoStep[];
/** Alle Fotoarten eines Raums in der Reihenfolge der Aufnahme (für Anzeige und KI). */
export const ROOM_PHOTO_ORDER = [...ROOM_WALL_STEPS, "wand_nische", ...ROOM_SMALL_STEPS, "rundum", "schrank_offen"] as const satisfies readonly PhotoStep[];

export const ROOM_PHOTO_MODES = ["waende", "kurz"] as const;
export type RoomPhotoMode = (typeof ROOM_PHOTO_MODES)[number];
/** Räume, die standardmäßig in der Kurzform (zwei Fotos) aufgenommen werden; der Kunde kann umschalten. */
export const SMALL_ROOM_TYPES: readonly RoomType[] = ["bad", "abstellraum", "balkon"];
export const defaultPhotoMode = (type: RoomType): RoomPhotoMode => (SMALL_ROOM_TYPES.includes(type) ? "kurz" : "waende");

/** Form eines Raums (rooms.room_shape, Migration 0031): nur gefragt, wenn der Kunde „Ecke oder Nische“ antippt. */
export const ROOM_SHAPES = ["rechteckig", "l_foermig", "nische"] as const;
export type RoomShape = (typeof ROOM_SHAPES)[number];
export const ROOM_SHAPE_LABEL: Record<RoomShape, string> = { rechteckig: "Rechteckig", l_foermig: "L-förmig (mit Ecke)", nische: "Mit Nische" };

/** Wände eines Raums, von der Zimmertür aus gesehen. */
export const ROOM_WALLS = ["gegenueber", "rechts", "links", "tuer", "nische"] as const;
export type RoomWall = (typeof ROOM_WALLS)[number];
export const ROOM_WALL_LABEL: Record<RoomWall, string> = {
  gegenueber: "gegenüberliegende Wand",
  rechts: "rechte Wand",
  links: "linke Wand",
  tuer: "Wand mit der Tür",
  nische: "Ecke / Nische",
};
export const WALL_OF_STEP: Partial<Record<PhotoStep, RoomWall>> = {
  wand_gegenueber: "gegenueber",
  wand_rechts: "rechts",
  wand_links: "links",
  wand_tuer: "tuer",
  wand_nische: "nische",
};

/**
 * Türen je Raum (15.10.2026, rooms.doors, Migration 0032): Was die KI auf den Wandfotos findet bzw. der Kunde markiert,
 * mit Entscheidung: raum = führt in einen Raum (to), keine = keine Tür / gehört nicht dazu, eingang = die Tür, durch
 * die der Kunde hereingekommen ist.
 */
export const ROOM_DOOR_KINDS = ["tuer", "durchgang", "fenstertuer"] as const;
export type RoomDoorKind = (typeof ROOM_DOOR_KINDS)[number];
export const ROOM_DOOR_KIND_LABEL: Record<RoomDoorKind, string> = { tuer: "Tür", durchgang: "Durchgang", fenstertuer: "Balkon-/Terrassentür" };
export const ROOM_DOOR_STATUS = ["raum", "keine", "eingang"] as const;
export type RoomDoorStatus = (typeof ROOM_DOOR_STATUS)[number];
export type RoomDoor = {
  id: string;
  photoId: string | null;
  x: number | null;
  y: number | null;
  wall: RoomWall | null;
  kind: RoomDoorKind;
  status: RoomDoorStatus;
  /** Raum hinter der Tür (status raum). */
  to: string | null;
  source: "ki" | "kunde";
};
export const ROOM_DOOR_MAX = 20;

/**
 * Lampen ab- und anbauen (15.10.2026, Schritt 5 von 5): eigener, freiwilliger Weg nach den Räumen (surveys.lamps,
 * Migration 0034). Je Raum die Lampen mit Art; Abbau in der alten, Anbau in der neuen Wohnung getrennt; dazu neue
 * Lampen nur für die neue Wohnung. Die Firma rechnet Minuten je Art (Rechenwerte lampMinutes).
 */
export const LAMP_KINDS = ["plafonniere", "pendel", "kronleuchter", "strahler", "ventilator", "wandleuchte"] as const;
export type LampKind = (typeof LAMP_KINDS)[number];
export const LAMP_KIND_LABEL: Record<LampKind, string> = {
  plafonniere: "Flache Deckenleuchte",
  pendel: "Pendelleuchte",
  kronleuchter: "Kronleuchter / Lüster",
  strahler: "Strahler / Spotschiene",
  ventilator: "Deckenventilator mit Licht",
  wandleuchte: "Wandleuchte",
};
export type LampItem = { id: string; roomId: string; kind: LampKind; abbau: boolean; anbau: boolean };
export type LampsInfo = {
  /** Soll die Firma Lampen ab- oder anbauen? null = noch nicht gefragt. */
  wanted: boolean | null;
  items: LampItem[];
  /** Neue Lampen nur für die neue Wohnung (Anbau), je Art. */
  neu: Partial<Record<LampKind, number>>;
};
export const LAMP_MAX = 60;

/** Räume, in denen große Schränke typisch sind – nur dort wird gefragt. */
export const CABINET_ROOM_TYPES: readonly RoomType[] = ["wohnen", "schlafen", "kind", "arbeit"];

/** Pakete (plans, CHECK in Migration 0011). Preise netto in Cent; Monatspakete zum Monatsende, Jahrespakete zum Jahresende kündbar. */
export const PLAN_IDS = ["start", "profi", "business", "flotte"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/**
 * moebelplanCents: Zusatzleistung Möbelplan (15.10.2026), netto je angefangenem Monat, steigt mit dem Paket (Betreiber:
 * kein Pauschalpreis für alle Pakete). Vorläufig etwa die Hälfte des Monatspreises; vor der Freischaltung
 * (MOEBELPLAN_BUCHBAR) bestätigt der Betreiber die Beträge.
 */
export const PLANS: readonly { id: PlanId; name: string; captures: number; monthCents: number; yearCents: number; extraCents: number; moebelplanCents: number }[] = [
  { id: "start", name: "Start", captures: 15, monthCents: 1990, yearCents: 19900, extraCents: 190, moebelplanCents: 990 },
  { id: "profi", name: "Profi", captures: 50, monthCents: 4990, yearCents: 49900, extraCents: 150, moebelplanCents: 2490 },
  { id: "business", name: "Business", captures: 150, monthCents: 9990, yearCents: 99900, extraCents: 100, moebelplanCents: 4990 },
  { id: "flotte", name: "Flotte", captures: 500, monthCents: 19990, yearCents: 199900, extraCents: 70, moebelplanCents: 9990 },
];
/** Möbelplan-Preis des Pakets (netto je Monat); null ohne Paket. */
export const moebelplanCents = (planId: string | null | undefined): number | null => PLANS.find((p) => p.id === planId)?.moebelplanCents ?? null;
/**
 * Privatkunden (Vorgabe 08.10.2026): 1,99 € je Aufnahme, brutto inkl. 19 % USt.
 * Bezahlt nach der Erfassung, vor der Auswertung. Keine Gratis-Version.
 */
export const PRIVATE_CAPTURE_CENTS = 199;
/** Probe nach bestätigter E-Mail, einmalig. */
export const TRIAL_CAPTURE_COUNT = 3;

/** Einmal-Links per E-Mail (Tabelle auth_tokens, CHECK in Migration 0009). */
export const AUTH_TOKEN_KINDS = ["email_bestaetigen", "passwort_zuruecksetzen", "email_aendern"] as const;
export type AuthTokenKind = (typeof AUTH_TOKEN_KINDS)[number];

export const USER_ROLES = ["owner", "member"] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Grobe Füllmenge eines Raums (Kleinteile, Schrankinhalt), schätzt die KI. */
export const FILL_LEVELS = ["gering", "normal", "voll"] as const;
export type FillLevel = (typeof FILL_LEVELS)[number];

/** Firmenarten (Migration 0012, 0027): "privat" = interne Firma für die Kunden-Version, "demo" = Demo-Weg ohne Anmeldung. */
export const COMPANY_KINDS = ["firma", "privat", "demo"] as const;
export type CompanyKind = (typeof COMPANY_KINDS)[number];
export const PRIVATE_COMPANY_SLUG = "umzugsscan-privat";
/** Demo-Weg (Migration 0027): Aufnahme ohne Anmeldung über /b/umzugsscan-demo, Kurzlink scan.umzugsscan.app/demo. */
export const DEMO_COMPANY_SLUG = "umzugsscan-demo";
/** Höchstens so viele Demo-Auswertungen je Tag (deutsche Zeit), alle Besucher zusammen: begrenzt die KI-Kosten. */
// 30.10.2026 (Betreiber: Demo kann fressen, Inkognito-Tabs): 15 statt 30 am Tag, je Anschluss nur 1 Demo in 24 Stunden,
// höchstens ein Viertel des KI-Tagesbudgets (der Rest bleibt für echte Kunden).
export const DEMO_DAILY_LIMIT = 15;
export const DEMO_PER_IP_PER_DAY = 1;
export const DEMO_BUDGET_SHARE = 0.25;
/** Demo-Aufnahmen samt Fotos werden nach so vielen Stunden gelöscht. */
export const DEMO_RETENTION_HOURS = 24;

/** Messung ohne Cookies (Migration 0027): Tageszähler je Art und Schlüssel, keine Personen, keine IP-Adressen. */
export const METRIC_KINDS = ["seite", "quelle", "registrieren", "demo_start"] as const;
export type MetricKind = (typeof METRIC_KINDS)[number];
/** Herkunftsmerkmal ?von= (Website, Privat-PDF, Demo): kleine Buchstaben, Ziffern, Bindestrich. */
export const SOURCE_PATTERN = /^[a-z0-9-]{1,40}$/;
/** Belegzahlen zur Genauigkeit erscheinen auf der Website erst ab so vielen Aufnahmen mit tatsächlich geladenem Volumen. */
export const ACCURACY_MIN_CASES = 20;
/**
 * Schritte der Kundenerfassung in Reihenfolge, für die Abbruch-Auswertung im Admin-Bereich.
 * Muss FLOW in apps/capture/src/ablauf.ts entsprechen (Test dort). Nebenwege, Unterseiten und ältere Schritte zählen
 * wie ihr Schritt (CAPTURE_STEP_ALIAS).
 */
export const CAPTURE_STEPS = ["willkommen", "grundriss", "eingang", "fotos", "besonderes", "kueche", "leistungen", "kontakt", "auszug", "einzug", "pruefung"] as const;
export const CAPTURE_STEP_ALIAS: Record<string, (typeof CAPTURE_STEPS)[number]> = {
  raeume: "eingang",
  termin: "kontakt",
  // Bis 30.10.2026 eigene Seiten, seitdem zusammengelegt.
  markieren: "besonderes",
  sonderteile: "besonderes",
  zerbrechliches: "besonderes",
  lampen: "leistungen",
};
export const CAPTURE_STEP_LABEL: Record<(typeof CAPTURE_STEPS)[number], string> = {
  willkommen: "Start und Einwilligung",
  eingang: "Flurfotos",
  grundriss: "Flur zeichnen",
  fotos: "Fotos je Raum",
  besonderes: "Besonderes",
  kueche: "Küche",
  leistungen: "Leistungen",
  kontakt: "Kontakt und Termin",
  auszug: "Auszug",
  einzug: "Einzug",
  pruefung: "Prüfung",
};
/** Kunden-Version: Code/Link zur Anmeldung gilt 30 Minuten, höchstens 5 Fehlversuche. */
export const CUSTOMER_CODE_MINUTES = 30;
export const CUSTOMER_CODE_ATTEMPTS = 5;

/** Übernahme einer Privat-Aufnahme in ein Firmenkonto (Migration 0013). */
export const TRANSFER_STATUSES = ["angefragt", "bestaetigt", "abgelehnt"] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];
/** So lange gilt der Zustimmungslink in der Mail an die Kundin. */
export const TRANSFER_DAYS = 14;

// ------------------------------------------------------------ Abrechnung (Stripe, Migration 0014)

export const SUBSCRIPTION_STATUSES = ["active", "trialing", "past_due", "unpaid", "canceled", "incomplete", "incomplete_expired", "paused"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
export const BILLING_INTERVALS = ["monat", "jahr"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/** EU-Länder (ISO 3166-1 alpha-2) für Reverse Charge; DE selbst immer 19 %. */
export const EU_COUNTRIES = ["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK"] as const;

// ------------------------------------------------------------ Sonderteile und Zerbrechliches (Migration 0016)

/** Eigene Zwischenschritte der Erfassung (08.10.2026). */
export const SPECIAL_KINDS = ["sonderteil", "zerbrechlich"] as const;
export type SpecialKind = (typeof SPECIAL_KINDS)[number];

export const SPECIAL_ITEMS: Record<SpecialKind, readonly { key: string; label: string; hint?: string }[]> = {
  sonderteil: [
    { key: "klavier", label: "Klavier oder Flügel" },
    { key: "tresor", label: "Tresor" },
    { key: "aquarium", label: "Aquarium", hint: "mit Größe in der Notiz" },
    { key: "fitness", label: "Fitnessgerät", hint: "z. B. Laufband, Hantelbank, Rudergerät" },
    { key: "waschmaschine", label: "Waschmaschine oder Trockner" },
    { key: "schwer", label: "Sehr schweres Teil", hint: "über ca. 80 kg, z. B. Massivholzschrank" },
    { key: "sperrig", label: "Sehr sperriges Teil", hint: "passt kaum durch Tür oder Treppenhaus" },
  ],
  zerbrechlich: [
    { key: "glas", label: "Glas", hint: "Glastisch, Vitrine, Glasplatten" },
    { key: "spiegel", label: "Großer Spiegel" },
    { key: "kunst", label: "Kunst", hint: "Gemälde, Skulpturen" },
    { key: "antik", label: "Antiquitäten" },
    { key: "elektronik", label: "Empfindliche Elektronik", hint: "PC, HiFi, Musikinstrumente" },
    { key: "fernseher", label: "Fernseher" },
    { key: "sonstiges", label: "Sonstiges Zerbrechliches" },
  ],
};
export const SPECIAL_ITEM_KEYS = [...new Set(Object.values(SPECIAL_ITEMS).flatMap((l) => l.map((i) => i.key)))];
export type SpecialItem = { kind: SpecialKind; key: string; qty: number; note: string | null };

// ------------------------------------------------------------ Grundriss (Migration 0017)

/** Seite einer Tür im Flur, gesehen vom Eingang in Gehrichtung. */
/** anfang (30.10.2026, Migration 0038): Wand am Eingang (Stirnseite von Abschnitt 1, neben oder statt der Wohnungstür). */
export const HALL_SIDES = ["links", "rechts", "ende", "anfang"] as const;
export type HallSide = (typeof HALL_SIDES)[number];
export const HALL_SIDE_LABEL: Record<HallSide, string> = { links: "links", rechts: "rechts", ende: "am Ende", anfang: "an der Eingangswand" };
/** Ungefähre Form eines Flurs. */
export const HALL_LAYOUTS = ["gerade", "l_foermig", "t_foermig", "offen", "unklar"] as const;
export type HallLayout = (typeof HALL_LAYOUTS)[number];
/**
 * Flur als Weg aus Abschnitten (14.10.2026, Betreiber: Flure mit Ecke, Abzweig oder als Diele wurden nicht sauber erfasst).
 * Form: vom Kunden gewählt. Abschnitt: start = vom Eingang, ecke = nach einer Ecke (richtung = Abbiegung in Gehrichtung),
 * abzweig = geht vom Abschnitt `von` nach links/rechts ab (richtung = Seite in Gehrichtung des Hauptwegs).
 */
export const HALL_SHAPES = ["gerade", "ecke", "abzweig", "diele"] as const;
export type HallShape = (typeof HALL_SHAPES)[number];
export const HALL_SHAPE_LABEL: Record<HallShape, string> = { gerade: "Gerade", ecke: "Um die Ecke", abzweig: "Mit Abzweig", diele: "Eher ein Raum (Diele)" };
export const HALL_SECTION_KINDS = ["start", "ecke", "abzweig"] as const;
export type HallSectionKind = (typeof HALL_SECTION_KINDS)[number];
/** geradeaus (31.10.2026): nur für Gänge, die von einer Diele abgehen (an der Wand gegenüber dem Eingang). */
export const HALL_TURNS = ["links", "rechts", "geradeaus"] as const;
export type HallTurn = (typeof HALL_TURNS)[number];
/** bei: Lage des Abzweigs am Ausgangsabschnitt (0 = Anfang, 1 = Ende), seit 30.10.2026 vom Kunden in der Skizze gesetzt. */
/** kurz: nur am Start – die Wohnungstür liegt direkt an einer Ecke, geradeaus geht kein Gang weiter (30.10.2026). */
/** eingang: nur am Start – wo die Wohnungstür in Abschnitt 1 liegt (anfang = Stirnseite unten, sonst seitlich; pos 0..1), 30.10.2026. */
export type HallEntrance = { seite: "anfang" | "links" | "rechts"; pos: number };
/**
 * Flur als Baum (31.10.2026, Betreiber: „das ultimative Flurwerkzeug, der Flur ist der Kern jeder Wohnung“): Jeder Gang hängt
 * an seinem Gang `von` – eine Ecke am Ende (zwei Ecken am selben Ende = T), ein Abzweig an der Stelle `bei` (zwei an derselben
 * Stelle = Kreuzung). laenge: Länge des Gangs relativ zur Standardlänge (Ecke verschieben), ohne Maßstab.
 */
export type HallSection = {
  nr: number;
  art: HallSectionKind;
  richtung: HallTurn | null;
  von: number | null;
  bei?: number | null;
  kurz?: boolean;
  eingang?: HallEntrance | null;
  laenge?: number | null;
};
/** Grenzen für HallSection.laenge (Anteil der Standardlänge). */
export const HALL_LENGTH_MIN = 0.3;
export const HALL_LENGTH_MAX = 1.8;
/** Höchstens so viele Abschnitte je Flur. */
export const HALL_SECTION_MAX = 9;

/** Etagen: −1 Keller … 0 Wohnungsebene/Erdgeschoss … bis 10. */
export const FLOOR_MIN = -3;
export const FLOOR_MAX = 10;
export function floorLabel(f: number): string {
  if (f < 0) return f === -1 ? "Keller" : `${-f}. Untergeschoss`;
  if (f === 0) return "Eingangsebene";
  return f === 1 ? "Obergeschoss" : `${f}. Obergeschoss`;
}
/** Öffnungen, die die Flur-Erkennung meldet. */
export const OPENING_KINDS = ["tuer", "durchgang", "treppe_hoch", "treppe_runter"] as const;
export type OpeningKind = (typeof OPENING_KINDS)[number];

// ------------------------------------------------------------ Küche (Migration 0018)

/** Antworten auf "Kommt die Küche mit?" und "Kommt der Inhalt mit?". */
export const KITCHEN_ANSWERS = ["ja", "nein"] as const;
export type KitchenAnswer = (typeof KITCHEN_ANSWERS)[number];
/** Geräte, die der Kunde einzeln angibt (Katalogschlüssel). */
export const KITCHEN_APPLIANCES = [
  "kuehlschrank",
  "kuehl_gefrier",
  "gefriertruhe",
  "herd",
  "spuelmaschine",
  "mikrowelle",
  "dunstabzug",
  "waschmaschine",
  "trockner",
] as const;
export type KitchenAppliance = (typeof KITCHEN_APPLIANCES)[number];
export const KITCHEN_APPLIANCE_LABEL: Record<KitchenAppliance, string> = {
  kuehlschrank: "Kühlschrank",
  kuehl_gefrier: "Kühl-Gefrier-Kombination",
  gefriertruhe: "Gefriertruhe / Gefrierschrank",
  herd: "Herd / Backofen",
  spuelmaschine: "Spülmaschine",
  mikrowelle: "Mikrowelle",
  dunstabzug: "Dunstabzugshaube",
  waschmaschine: "Waschmaschine",
  trockner: "Wäschetrockner",
};
export const APPLIANCE_ACTIONS = ["mit", "bleibt", "entsorgen"] as const;
export type ApplianceAction = (typeof APPLIANCE_ACTIONS)[number];
export const APPLIANCE_ACTION_LABEL: Record<ApplianceAction, string> = { mit: "kommt mit", bleibt: "bleibt", entsorgen: "entsorgen" };
/** Angaben zur Küche (surveys.kitchen); leer = nicht gefragt. */
export type KitchenInfo = {
  moves?: KitchenAnswer;
  contents?: KitchenAnswer;
  appliances?: Partial<Record<KitchenAppliance, ApplianceAction>>;
};

// ------------------------------------------------------------ Zugang und Parken (Migration 0020, optional)
// Bezeichnungen und Berichtszeilen: zugang.ts
export const STAIRWELL_TYPES = ["normal", "eng", "wendel"] as const;
export type StairwellType = (typeof STAIRWELL_TYPES)[number];
export const ELEVATOR_SIZES = ["klein", "gross", "lastenaufzug"] as const;
export type ElevatorSize = (typeof ELEVATOR_SIZES)[number];
export const STEPS_TO_DOOR = ["keine", "wenige", "viele"] as const;
export type StepsToDoor = (typeof STEPS_TO_DOOR)[number];
export const PARKING_OPTIONS = ["frei", "halteverbot_noetig", "halteverbot_vorhanden", "unklar"] as const;
export type ParkingOption = (typeof PARKING_OPTIONS)[number];
export const DRIVEWAY_TYPES = ["strasse", "hof_tor", "eng", "fussgaengerzone"] as const;
export type DrivewayType = (typeof DRIVEWAY_TYPES)[number];

// ------------------------------------------------------------ Angebotsstand (Migration 0022)
/** Wie weit die Firma mit dem Angebot ist (nur intern, hat nichts mit dem Erfassungsstatus zu tun). */
export const OFFER_STATUS = ["offen", "angebot_gesendet", "gewonnen", "verloren"] as const;
export type OfferStatus = (typeof OFFER_STATUS)[number];
export const OFFER_STATUS_LABEL: Record<OfferStatus, string> = {
  offen: "Angebot offen",
  angebot_gesendet: "Angebot gesendet",
  gewonnen: "Gewonnen",
  verloren: "Verloren",
};

// ------------------------------------------------------------ Team (Migration 0024)
/** Höchstens so viele Konten (Inhaber + Mitarbeiter, einschließlich offener Einladungen) je Firma. */
export const TEAM_MAX_USERS = 10;

// ------------------------------------------------------------ Empfehlungsprogramm (Migration 0025)
/** Form des persönlichen Empfehlungscodes (Link: app…/registrieren?ref=CODE). */
export const REFERRAL_CODE_RE = /^[A-Z0-9]{6,12}$/;
/** Ein Werber bekommt höchstens so viele Gratismonate in 12 Monaten. */
export const REFERRAL_MAX_PER_YEAR = 12;

/**
 * Beta-Preis (30.10.2026, Betreiber): 70 % Rabatt auf Monatspakete in den ersten 6 Monaten, Jahrespakete 35 % auf das erste
 * Jahr (entspricht 6 Monaten zu −70 %), für Buchungen bis einschließlich bookUntil (deutsche Zeit). Stripe-Gutscheine in
 * server/src/abrechnung/stripe.ts, Hinweis auf /preise und in AGB § 5.
 */
export const BETA_DISCOUNT = { monthPct: 70, months: 6, yearPct: 35, bookUntil: "2027-03-31" } as const;
/** Gilt der Beta-Preis für eine Buchung an diesem Tag? */
export const betaDiscountActive = (now = new Date()) => now.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" }) <= BETA_DISCOUNT.bookUntil;
